/**
 * Persist desk knobs across refresh / deep-link (S1 / #112), scoped per deal.
 * URL hash query wins over localStorage; both beat package defaults.
 *
 * Hash shape: #decision?hh=3.42&bid=13&bos=0&nb=0&disc=0.1&aro=0&oh=other:53000:0
 * Storage: `d2d.energy.desk-knobs.<dealId>` (legacy Eagle: `eagle-desk-knobs-v1`).
 */

/** @deprecated Prefer deskStorageKey(dealId) — kept for Eagle one-time migrate. */
export const DESK_STORAGE_KEY = 'eagle-desk-knobs-v1';
export const DESK_STORAGE_KEY_PREFIX = 'd2d.energy.desk-knobs.';
export const DESK_QUERY_PREFIX = '';
export const DEFAULT_DESK_DEAL_ID = 'eagle-etx-2026';

/** Active deal for unscoped API callers (Decision/Sensitivity should pass dealId). */
let activeDealId = DEFAULT_DESK_DEAL_ID;
/** In-memory desk knobs for the current page session (beats hash/storage on nav). */
let liveDeskKnobs = null;
let liveDealId = null;

export function deskStorageKey(dealId = activeDealId) {
  const id = String(dealId || DEFAULT_DESK_DEAL_ID).trim() || DEFAULT_DESK_DEAL_ID;
  return `${DESK_STORAGE_KEY_PREFIX}${id}`;
}

/** Switch deal context and drop in-memory live knobs so HH/WTI cannot leak. */
export function setDeskDealContext(dealId) {
  const next = String(dealId || DEFAULT_DESK_DEAL_ID).trim() || DEFAULT_DESK_DEAL_ID;
  if (next !== activeDealId) {
    liveDeskKnobs = null;
    liveDealId = null;
  }
  activeDealId = next;
  return activeDealId;
}

export function getDeskDealContext() {
  return activeDealId;
}

const NUMERIC_FIELDS = [
  'hh',
  'bidPriceMM',
  'boswellVolAdj',
  'netbackAdj',
  'discountRate',
  'aroMM',
];

const QUERY_KEYS = {
  hh: 'hh',
  bidPriceMM: 'bid',
  boswellVolAdj: 'bos',
  netbackAdj: 'nb',
  discountRate: 'disc',
  aroMM: 'aro',
  buyerOverheadCategories: 'oh',
};

/** Desk knob query keys — selection/drilldown keys must survive syncDeskToHash. */
export const DESK_QUERY_KEY_SET = new Set(Object.values(QUERY_KEYS));

function num(v, fallback = null) {
  if (v == null || v === '') return fallback;
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
}

/** Parse a controlled numeric text field — empty stays empty for UX; invalid → fallback. */
export function parseNumericDraft(raw, { empty = null, fallback = 0, min = null, max = null } = {}) {
  if (raw === '' || raw == null) return empty;
  const n = Number(raw);
  if (!Number.isFinite(n)) return fallback;
  let out = n;
  if (min != null && out < min) out = min;
  if (max != null && out > max) out = max;
  return out;
}

/** Coerce draft → finite number for the engine (never NaN). */
export function coerceFinite(value, fallback = 0) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

export function splitHash(rawHash = typeof window !== 'undefined' ? window.location.hash : '') {
  const raw = String(rawHash || '').replace(/^#/, '');
  const qIdx = raw.indexOf('?');
  if (qIdx < 0) return { path: raw, query: '' };
  return { path: raw.slice(0, qIdx), query: raw.slice(qIdx + 1) };
}

export function encodeOhCategories(categories) {
  if (!Array.isArray(categories) || !categories.length) return '';
  return categories
    .map((c) => {
      const id = encodeURIComponent(String(c.id || ''));
      const monthly = coerceFinite(c.monthly, 0);
      const inc = c.includeInCf ? 1 : 0;
      return `${id}:${monthly}:${inc}`;
    })
    .join(',');
}

export function decodeOhCategories(raw) {
  if (!raw || typeof raw !== 'string') return null;
  const cats = [];
  for (const part of raw.split(',')) {
    if (!part) continue;
    const [idEnc, monthlyRaw, incRaw] = part.split(':');
    if (!idEnc) continue;
    let id;
    try {
      id = decodeURIComponent(idEnc);
    } catch {
      id = idEnc;
    }
    cats.push({
      id,
      monthly: Math.max(0, coerceFinite(monthlyRaw, 0)),
      includeInCf: incRaw === '1' || incRaw === 'true',
    });
  }
  return cats.length ? cats : null;
}

/** Compact query string from a desk knobs object (only defined fields). */
export function encodeDeskQuery(knobs = {}) {
  const params = new URLSearchParams();
  for (const field of NUMERIC_FIELDS) {
    const key = QUERY_KEYS[field];
    if (knobs[field] == null || knobs[field] === '') continue;
    const n = Number(knobs[field]);
    if (!Number.isFinite(n)) continue;
    params.set(key, String(n));
  }
  if (Array.isArray(knobs.buyerOverheadCategories) && knobs.buyerOverheadCategories.length) {
    params.set(QUERY_KEYS.buyerOverheadCategories, encodeOhCategories(knobs.buyerOverheadCategories));
  }
  return params.toString();
}

/** Parse desk knobs from a query string (hash query or search). */
export function decodeDeskQuery(query = '') {
  if (!query) return null;
  const params = new URLSearchParams(query.startsWith('?') ? query.slice(1) : query);
  const out = {};
  let any = false;
  for (const field of NUMERIC_FIELDS) {
    const key = QUERY_KEYS[field];
    if (!params.has(key)) continue;
    const n = num(params.get(key), null);
    if (n == null) continue;
    out[field] = n;
    any = true;
  }
  if (params.has(QUERY_KEYS.buyerOverheadCategories)) {
    const cats = decodeOhCategories(params.get(QUERY_KEYS.buyerOverheadCategories));
    if (cats) {
      out.buyerOverheadCategories = cats;
      any = true;
    }
  }
  return any ? out : null;
}

export function readDeskFromHash(rawHash) {
  const { query } = splitHash(rawHash);
  return decodeDeskQuery(query);
}

function parseDeskStoragePayload(raw) {
  if (!raw) return null;
  const parsed = JSON.parse(raw);
  if (!parsed || typeof parsed !== 'object') return null;
  const out = {};
  let any = false;
  for (const field of NUMERIC_FIELDS) {
    if (parsed[field] == null || parsed[field] === '') continue;
    const n = Number(parsed[field]);
    if (!Number.isFinite(n)) continue;
    out[field] = n;
    any = true;
  }
  if (Array.isArray(parsed.buyerOverheadCategories) && parsed.buyerOverheadCategories.length) {
    out.buyerOverheadCategories = parsed.buyerOverheadCategories.map((c) => ({
      id: String(c.id),
      label: c.label,
      monthly: Math.max(0, coerceFinite(c.monthly, 0)),
      includeInCf: !!c.includeInCf,
    }));
    any = true;
  }
  return any ? out : null;
}

export function readDeskFromStorage(
  storage = typeof localStorage !== 'undefined' ? localStorage : null,
  dealId = activeDealId,
) {
  if (!storage) return null;
  try {
    const key = deskStorageKey(dealId);
    let out = parseDeskStoragePayload(storage.getItem(key));
    // One-time Eagle migrate from legacy unscoped key → scoped key.
    if (
      !out &&
      (dealId === DEFAULT_DESK_DEAL_ID || dealId === 'eagle-etx-2026') &&
      key !== DESK_STORAGE_KEY
    ) {
      out = parseDeskStoragePayload(storage.getItem(DESK_STORAGE_KEY));
      if (out) {
        try {
          storage.setItem(key, JSON.stringify({ v: 2, dealId, migratedFrom: DESK_STORAGE_KEY, ...out }));
        } catch {
          /* ignore */
        }
      }
    }
    return out;
  } catch {
    return null;
  }
}

export function writeDeskToStorage(
  knobs,
  storage = typeof localStorage !== 'undefined' ? localStorage : null,
  dealId = activeDealId,
) {
  if (!storage) return false;
  try {
    const prior = readDeskFromStorage(storage, dealId) || {};
    const mergedCats =
      Array.isArray(knobs.buyerOverheadCategories) && knobs.buyerOverheadCategories.length
        ? knobs.buyerOverheadCategories
        : prior.buyerOverheadCategories;
    const payload = {
      v: 2,
      dealId: dealId || DEFAULT_DESK_DEAL_ID,
      savedAt: new Date().toISOString(),
      hh: knobs.hh != null ? knobs.hh : prior.hh,
      bidPriceMM: knobs.bidPriceMM != null ? knobs.bidPriceMM : prior.bidPriceMM,
      boswellVolAdj: knobs.boswellVolAdj != null ? knobs.boswellVolAdj : prior.boswellVolAdj,
      netbackAdj: knobs.netbackAdj != null ? knobs.netbackAdj : prior.netbackAdj,
      discountRate: knobs.discountRate != null ? knobs.discountRate : prior.discountRate,
      aroMM: knobs.aroMM != null ? knobs.aroMM : prior.aroMM,
      buyerOverheadCategories: Array.isArray(mergedCats)
        ? mergedCats.map((c) => ({
            id: c.id,
            label: c.label,
            monthly: Math.max(0, coerceFinite(c.monthly, 0)),
            includeInCf: !!c.includeInCf,
          }))
        : undefined,
    };
    storage.setItem(deskStorageKey(dealId), JSON.stringify(payload));
    return true;
  } catch {
    return false;
  }
}

/**
 * Merge priority: URL hash query > localStorage > defaults (deep-link restore).
 * For in-app tab nav after a live desk edit, use loadDeskKnobsForNav() instead —
 * that prefers the in-memory live snapshot so neither a stale hash debounce nor a
 * prior-session storage row can clobber deep-link or just-edited knobs incorrectly.
 */
export function loadDeskKnobs(defaults = {}, { hash, storage, dealId } = {}) {
  const id = dealId || activeDealId;
  const fromHash = readDeskFromHash(hash ?? (typeof window !== 'undefined' ? window.location.hash : ''));
  const fromStore = readDeskFromStorage(
    storage ?? (typeof localStorage !== 'undefined' ? localStorage : null),
    id,
  );
  const merged = { ...defaults };
  if (fromStore) Object.assign(merged, fromStore);
  if (fromHash) Object.assign(merged, fromHash);
  // Merge OH categories by id when both sides contribute.
  if (fromStore?.buyerOverheadCategories || fromHash?.buyerOverheadCategories) {
    const base = defaults.buyerOverheadCategories || [];
    const overlay = fromHash?.buyerOverheadCategories || fromStore?.buyerOverheadCategories || [];
    const byId = new Map(base.map((c) => [c.id, { ...c }]));
    for (const c of overlay) {
      const prev = byId.get(c.id) || { id: c.id, label: c.label || c.id, monthly: 0, includeInCf: false };
      byId.set(c.id, {
        ...prev,
        ...c,
        monthly: Math.max(0, coerceFinite(c.monthly, prev.monthly)),
        includeInCf: !!c.includeInCf,
      });
    }
    merged.buyerOverheadCategories = [...byId.values()];
  }
  return merged;
}

function assignDefined(target, source = {}) {
  for (const [key, value] of Object.entries(source)) {
    if (value == null || value === '') continue;
    target[key] = value;
  }
  return target;
}

/** Remember the latest desk knobs from Decision/Sensitivity and persist to storage.
 * Merges with the prior live snapshot so Sensitivity (no bid) cannot wipe Decision bid.
 * Refuses writes when `dealId` is not the active desk context — prevents a mounted
 * Decision/Sensitivity from copying Eagle HH into Juno's scoped key on deal switch.
 */
export function rememberLiveDeskKnobs(knobs = {}, { dealId, storage } = {}) {
  const id = dealId || activeDealId;
  if (id !== activeDealId) {
    return liveDeskKnobs;
  }
  if (liveDealId && liveDealId !== id) {
    liveDeskKnobs = null;
  }
  liveDealId = id;
  const next = { ...(liveDeskKnobs || {}) };
  for (const field of ['hh', 'bidPriceMM', 'boswellVolAdj', 'netbackAdj', 'discountRate', 'aroMM']) {
    if (knobs[field] == null || knobs[field] === '') continue;
    next[field] = knobs[field];
  }
  if (Array.isArray(knobs.buyerOverheadCategories)) {
    next.buyerOverheadCategories = knobs.buyerOverheadCategories.map((c) => ({ ...c }));
  }
  liveDeskKnobs = next;
  writeDeskToStorage(
    liveDeskKnobs,
    storage ?? (typeof localStorage !== 'undefined' ? localStorage : null),
    id,
  );
  return liveDeskKnobs;
}

export function clearLiveDeskKnobs() {
  liveDeskKnobs = null;
  liveDealId = null;
}

/**
 * Knobs for in-app navigation: live session > hash (deep-link) > storage > defaults.
 * Preserves deep-link hash when the desk has not edited this session, while still
 * carrying the latest edited knobs across tabs without waiting on hash debounce.
 * Only defined live fields are applied so a Sensitivity snapshot cannot null out bid.
 */
export function loadDeskKnobsForNav(defaults = {}, { hash, storage, dealId } = {}) {
  const id = dealId || activeDealId;
  const fromHash = readDeskFromHash(hash ?? (typeof window !== 'undefined' ? window.location.hash : ''));
  const fromStore = readDeskFromStorage(
    storage ?? (typeof localStorage !== 'undefined' ? localStorage : null),
    id,
  );
  const merged = { ...defaults };
  if (fromStore) assignDefined(merged, fromStore);
  if (fromHash) assignDefined(merged, fromHash);
  const live = liveDealId === id ? liveDeskKnobs : null;
  if (live) assignDefined(merged, live);
  if (live?.buyerOverheadCategories || fromHash?.buyerOverheadCategories || fromStore?.buyerOverheadCategories) {
    const base = defaults.buyerOverheadCategories || [];
    const overlay =
      live?.buyerOverheadCategories ||
      fromHash?.buyerOverheadCategories ||
      fromStore?.buyerOverheadCategories ||
      [];
    const byId = new Map(base.map((c) => [c.id, { ...c }]));
    for (const c of overlay) {
      const prev = byId.get(c.id) || { id: c.id, label: c.label || c.id, monthly: 0, includeInCf: false };
      byId.set(c.id, {
        ...prev,
        ...c,
        monthly: Math.max(0, coerceFinite(c.monthly, prev.monthly)),
        includeInCf: !!c.includeInCf,
      });
    }
    merged.buyerOverheadCategories = [...byId.values()];
  }
  return merged;
}

/** Write knobs into the current hash path via replaceState (no remount / history spam). */
export function syncDeskToHash(knobs, { path } = {}) {
  if (typeof window === 'undefined' || typeof history === 'undefined') return;
  const current = splitHash(window.location.hash);
  // If the live route already moved (nav/unmount), never force desk path back onto the URL.
  if (path && current.path && path !== current.path) return;
  const nextPath = current.path || path || 'decision';
  // Only sync on desk routes — never clobber well/abs/crm/admin hashes.
  if (!/^(decision|sensitivity)$/.test(nextPath)) return;
  // Merge with existing hash knobs so Sensitivity doesn't wipe Decision-only fields (bid).
  const existing = decodeDeskQuery(current.query) || {};
  const merged = { ...existing, ...knobs };
  if (!Array.isArray(knobs.buyerOverheadCategories)) {
    merged.buyerOverheadCategories = existing.buyerOverheadCategories;
  }
  const deskQ = encodeDeskQuery(merged);
  // Preserve non-desk query keys (selection context: well/month/scenario/concern, etc.).
  const nextParams = new URLSearchParams(deskQ);
  const priorParams = new URLSearchParams(current.query || '');
  for (const [k, v] of priorParams.entries()) {
    if (DESK_QUERY_KEY_SET.has(k)) continue;
    if (!nextParams.has(k)) nextParams.set(k, v);
  }
  const q = nextParams.toString();
  const next = q ? `${nextPath}?${q}` : nextPath;
  if (current.path === nextPath && current.query === q) return;
  const url = `${window.location.pathname}${window.location.search}#${next}`;
  history.replaceState(null, '', url);
}

/** Preserve desk query when navigating between dashboards. */
export function hashWithDeskQuery(dashKey, knobs) {
  const path = String(dashKey || 'decision').replace(/^#/, '').split('?')[0];
  const q = encodeDeskQuery(knobs || loadDeskKnobs());
  return q ? `${path}?${q}` : path;
}

/** True when restored knobs differ from package defaults (for restore banner).
 * Only compares fields present on both sides so Decision-only keys (bid) do not
 * false-trigger the Sensitivity restore banner.
 */
export function deskKnobsDiffer(a = {}, b = {}, { fields } = {}) {
  const keys = fields || NUMERIC_FIELDS;
  for (const field of keys) {
    if (a[field] == null || b[field] == null) continue;
    if (Number(a[field]) !== Number(b[field])) return true;
  }
  const ac = a.buyerOverheadCategories;
  const bc = b.buyerOverheadCategories;
  if (!Array.isArray(ac) || !Array.isArray(bc)) return false;
  if (ac.length !== bc.length) return true;
  for (let i = 0; i < ac.length; i += 1) {
    if (ac[i].id !== bc[i].id) return true;
    if (coerceFinite(ac[i].monthly) !== coerceFinite(bc[i].monthly)) return true;
    if (!!ac[i].includeInCf !== !!bc[i].includeInCf) return true;
  }
  return false;
}
